import Board from "../models/board";
import { ControllerSubject } from "../components/Subject";
import { apiResultSuccess, deleteSpending, getBudget } from "../apihelper";
import { LogSettings } from "../models/log-settings";
import { Category } from "../models/category";
import { calculatePercentage, 
            displayAmountWithUnit, 
            getCurrentMonthDateRange, 
            roundNumberToCurrency, 
            getMonthDateRange, 
            getDateProp, 
            dateRangesAreEqual 
} from "../global/utils";
import { Spending } from "../models/spending";
import { LABELS } from "../global/constants";

export default class BudgetController extends Board {
    constructor(
        id=-1, 
        title='', 
        type='', 
        orderIndex=-1,
        dateCreated='', 
        lastUpdated='', 
        hidden=false
    ) {
        super(id, title, type, orderIndex, dateCreated, lastUpdated, hidden);

        this.logs = [];
        this.categories = [];
        this.settings = [];
        this.offset = 0;
        
        this.subject = new ControllerSubject();
        this.observer = false;

        this.isFiltering = false;
        this.logsBackup = [];
        this.categoryTally = 0;
        this.categoryPercentage = 0;
        this.activeCategory = false;
        this.currentDateRange = getCurrentMonthDateRange();
        this.originalDateRange = this.currentDateRange;
        this.timeTraveling = false;

        this.bindFunctions();
    }

    bindFunctions() {
        this.reactToEvent = this.reactToEvent.bind(this);
        this.newLogSuccess = this.newLogSuccess.bind(this);
        this.saveSettingsSuccess = this.saveSettingsSuccess.bind(this);
        this.timeTravel = this.timeTravel.bind(this);
    }

    reactToEvent(event={}) {
        this.subject.runEvent(event);
    }

    // Create

    // Creates a new Spending from newData
    // Adds it to todos array
    // Notifies subject to reload
    addLog(logData={}, unit='', unitSuffix, front=false) {
        // this.object.createLog(newLog);

         if ( logData ) {
            const catId = logData.categoryId;
            let cat = false;

            if ( catId ) {
                cat = this.categories.get(catId)
            }

            const log = new Spending(logData, unit, unitSuffix, cat, this.getTallyId());
        
            if ( front ) {
                if ( this.isFiltering ) {
                    this.logsBackup.unshift(log);
                    
                } else {
                    this.logs.unshift(log);
                }
            } else {
                if ( this.isFiltering ) {
                    this.logsBackup.push(log);
                } else {
                    this.logs.push(log);
                }
            }
        }
    }

    createLogs(logs=[]) {
        const unit = this.settings.getUnit();
        const unitSuffix = this.settings.getUnitSuffix();

        logs.forEach(logData => {
            this.addLog(logData, unit, unitSuffix);
        });

        if ( this.isFiltering ) {
            this.filterLogsByCategory(this.activeCategory);
        }
    }

    createLog(logData={}) {
        const unit = this.settings.getUnit();
        const unitSuffix = this.settings.getUnitSuffix();

        this.addLog(logData, unit, unitSuffix, true);
    }

    newLogSuccess(result) {
        if ( result.settings ) {
			this.setSettings(result.settings);
		}

		if ( result.log ) {
            const logData = result.log;
            logData.animate = true;

            this.createLog(logData);
		}
    }

    // Read

    getLogs() {
        return this.logs;
    }

    getSettings() {
        return this.settings;
    }

    getCategories() {
        return this.categories;
    }

    getObject() {
        return this.object;
    }

    getLogIds() {
        const ids = [];

        this.logs.forEach(item => {
            ids.push(item.id);
        });
        
        return ids;
    }

    getIsFiltering() {
        return this.isFiltering;
    }

    getCategoryPercentage() {
        return this.categoryPercentage;
    }

    getActiveCategory() {
        return this.activeCategory;
    }

    getTallyId() {
        return this.settings.getTallyId();
    }

    getCurrentDateRange() {
        return this.currentDateRange;
    }

    getCurrentMonth() {
        if ( !this.currentDateRange ) {
            return false;
        }
        const lastDay = this.currentDateRange.date_end;

        if ( lastDay ) {
            return getDateProp(lastDay, "yearMonth");
        }
        return false;
    }

    isTimeTravelling() {
        return this.timeTraveling;
    }

    showIntro() {
        return this.settings.intro;
    }

    // Displays tally
    // Rounds it and formats it it using unit, unitSuffix
    // During filtering prepends category tally
    displayTally() {
        if ( this.settings.length === 0 ) {
            return false;
        } 
        const unit = this.settings.getUnit();
        const unitSuffix = this.settings.getUnitSuffix();        
        const tallyRounded = roundNumberToCurrency( this.settings.getTally() );
        const tally = this.isFiltering || this.logs.length > 0 ? tallyRounded : '0.00';

        if ( this.isFiltering ) {
            const catTallyFormated = roundNumberToCurrency(this.categoryTally);
            const catTallyText = displayAmountWithUnit(catTallyFormated, unit, unitSuffix);
            const tallyText = displayAmountWithUnit(tally, unit, unitSuffix);

            return `${catTallyText} ${LABELS.budgetTallyDivider} ${tallyText}`;
        }

        return displayAmountWithUnit(tally, unit, unitSuffix);
    }
    
    hasLogs() {
        return this.logs.length > 0;
    }

    fetchData() {
        this.fetchLogs();
    }

    async fetchLogs(animate=false) {
        return getBudget(
            (results) => {
                if ( results.settings ) {
                    this.setSettings(results.settings);
                } 

                if ( results.categories ) {
                    this.createCategories(results.categories);
                }

                if ( results.logs ) {
                    const logs = results.logs;
                    const logsLength = logs.length;

                    if ( logsLength > 0 && this.offset > 0 ) {
                        /*
                        if ( animate ) {
                            this.addAnimationAttribute(logs);
                        }
                        */

                        this.offset += logsLength;
                        this.appendLogs(logs);
                    } else {
                        this.setLogs(results.logs);
                    }
                }
            },
            {board_id: this.id, offset: this.offset, ...this.currentDateRange }
            
        )
    }

    async reloadData() {
        return this.fetchData();
    }

    // Update

    setLogs(newLogs=[]) {
        if ( this.isFiltering ) {
            this.logsBackup = [];
        } else {
            this.logs = [];
        }
        this.createLogs(newLogs);
    }

    appendLogs(logs=[]) {
        this.dataLogs = this.dataLogs.concat(logs);
        this.createLogs(logs);
    }
    

    setSettings(newSettings={}) {
        this.settings = new LogSettings(newSettings);
        this.subject.runReload();
    }

    saveSettingsSuccess(result={}) {
        if ( apiResultSuccess(result) ) {
            if ( result.settings ) {
                this.setSettings(result.settings);
            }
        }
    }

    createCategories(categories=[]) {
        this.categories = new Map();

        categories.forEach(cat => {
            this.categories.set(cat.id, new Category(cat.id, cat.name));
        });
    }

    catsToFormObject() {
        const cats = [];
        this.categories.forEach(cat => {
            cats.push( cat.toFormObject() )
        });
        return cats;
    }

    // Delete

    // Calls API to delete log
    deleteLog(spendingId=-1) {
        const postData = { id: spendingId, board_id: this.id, tally_id: this.getTallyId(), ...this.currentDateRange};

        try {
            deleteSpending(
                (result) => {

                    if ( apiResultSuccess(result) ) {
                        this.deleteLogSuccess({...result, id: postData.id});
                    }  
                }, postData
            )
        } catch {
            console.log('error deleting');
        } finally {
            
        }
    }

    // Called Upon Delete Log Success
	deleteLogSuccess(results={}) {
		if ( results.settings ) {
			this.setSettings(results.settings);
		}

		if ( results.id ) {
			this.deleteLogFromLogs(results.id);
		}
	}

    deleteLogFromLogs(id=-1) {
        if ( id !== -1 ) {
            this.logs = this.logs.filter(item => item.id !== id);

            if ( this.isFiltering ) {
                this.logsBackup = this.logsBackup.filter(item => item.id !== id);
                this.calculateCategoryTally();
            }
        }
    }
    

    // 
    // Category Filtering functions
    // 

    // Filter list by category
    filterLogsByCategory(category=false) {
        if ( !category || !category.id ) {
            return false;
        }

        if ( !this.isFiltering ) { 
            this.logsBackup = this.logs;
        } else {
            this.logs = this.logsBackup;
        }
        
        this.logs = this.logs.filter( item => item.categoryId === category.id );
        this.isFiltering = true
        this.calculateCategoryTally();
        this.categoryPercentage = calculatePercentage(  this.categoryTally , this.settings.getTally() );
        this.activeCategory = category;
        this.subject.runReload();
    }

    // Adds up a list of logs
    calculateTallyFromList(logs=[]) {
        if ( logs.length === 0 )  {
            return 0;
        }

        let tally = 0;
        this.logs.forEach(log => {
            const amount = log.amount;
            tally += amount;  
        });

        return tally;
    }

    // Calculates category tally using calculateTallyFromList fn
    // Then formats the tally
    // Returns zero if list is empty
    // Updates categoryTally property
    calculateCategoryTally() {
        if ( this.logs.length === 0 ) {
            this.categoryTally = '0.00';
            return false;
        }

        this.categoryTally = this.calculateTallyFromList(this.logs);;
    }

    // Clears category filter 
    // Restores logs from logsBackup
    clearCategoryFilter() {
        this.logs = this.logsBackup;
        this.categoryTally = 0;
        this.isFiltering = false;
        this.activeCategory = false;
        this.subject.runReload();
    }

    timeTravel(targetDate=new Date()) {
        this.currentDateRange = getMonthDateRange(targetDate);
        
        if ( dateRangesAreEqual(this.originalDateRange, this.currentDateRange) ) {
            this.timeTraveling = false;
        } else {
            this.timeTraveling = true;
        }            

        this.fetchLogs();
    }
}